<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <form-card
            :title="$t(tKey + '.appointment.title')"
            :description="$t(tKey + '.appointment.description')"
          >
            <div class="flex flex-col gap-y-5">
              <date-picker
                v-model="rawMountingTimeslot.startTime"
                :error="errors?.startTime"
                :label="$t('mountingTimeslot.startTime')"
                mode="dateTime"
              />
              <FormInput
                v-model="rawMountingTimeslot.size"
                :error="errors?.size"
                class="col-start-1 col-end-2"
                type="number"
                :label="$t('mountingTimeslot.size')"
                :placeholder="$t('mountingTimeslot.size')"
              />
              <InputDuration
                v-model="rawMountingTimeslot.duration"
                :error="errors?.duration"
                type="text"
                class="w-full"
                :label="$t('mountingTimeslot.duration')"
                placeholder="hh:mm:ss"
              />
              <Switch v-model="rawMountingTimeslot.active" :error="errors?.active" :label="$t('mountingTimeslot.active.DEFAULT')" :options="{ yes: $t('mountingTimeslot.active.true'), no: $t('mountingTimeslot.active.false') }" />
            </div>
          </form-card>
        </div>
        <div class="w-full text-center">
          <custom-button
            variant="primary"
            class="mt-15"
            :busy="loading"
            @click="save"
          >
            {{ $t('save') }}
          </custom-button>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  boolean, date, number, object,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import FormInput from '@utils/inputs/Input.vue';
import DatePicker from '@components/utils/inputs/DatePicker.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import { syncRef } from '@hooks/state';
import CustomButton from '@utils/CustomButton.vue';
import InputDuration from '@utils/inputs/InputDuration.vue';
import Switch from '@utils/inputs/Switch.vue';
import type { Mounting, MountingTimeslot} from '@/entities/Entities';

interface Props {
  open: boolean;
  mounting: Mounting;
  mountingTimeslot?: MountingTimeslot;
}

const emit = defineEmits(['update:open', 'update:mountingTimeslot', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalMountingTimeslot = useVModel(props, 'mountingTimeslot', emit);

const isNew = computed(() => !props.mountingTimeslot);
const tKey = computed(() =>
  (isNew.value ? 'pages.mountings.mounting.timeslots.modal.new' : 'pages.mountings.mounting.timeslots.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const initialData = (mountingTimeslot?: MountingTimeslot) =>
  (mountingTimeslot
    ? { ...mountingTimeslot }
    : {
      startTime: undefined,
      duration: undefined,
      size: undefined,
      active: false,
    });

const rawMountingTimeslot = syncRef(internalMountingTimeslot, (mountingTimeslot) => ({
  ...initialData(mountingTimeslot),
}));

const {
  data: mountingTimeslot,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    startTime: date().nullable().required(t('validation.notEmpty')),
    duration: number().nullable().required(t('validation.notEmpty')),
    size: number().nullable().required(t('validation.notEmpty')),
    active: boolean().nullable().required(t('validation.notEmpty')),
  }),
  rawMountingTimeslot,
);

function resetData() {
  rawMountingTimeslot.value = { ...initialData() } as unknown as typeof mountingTimeslot['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalMountingTimeslot.value = undefined;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.mountingTimeslot;
    const url = edit ? props.mountingTimeslot['@id'] : '/mounting_timeslots';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        mounting: props.mounting['@id'],
        startTime: mountingTimeslot.value.startTime,
        duration: mountingTimeslot.value.duration,
        size: mountingTimeslot.value.size,
        active: mountingTimeslot.value.active,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>

